import { Dropdown, DropdownItem } from '@acies/ui';
import { SearchResult } from '../../../domain/search-results';

/**
 * How an ItemConfigurationDropdown fetches the configurations one item comes in, at the link the engine names the list by.
 *
 * An array is an answer: these are the configurations the item comes in. Anything else is the list not having been fetched at all, and the dropdown goes on offering nothing until it is asked again.
 */
export type ListConfigurations = (source: string) => Promise<ReadonlyArray<SearchResult> | Error>;

/**
 * The configuration a stock position's item comes in, picked from the handful that item is had in.
 *
 * What is listed is not decided here: {@link ItemConfigurationDropdown.list} is set by whoever puts one on screen, the way a SearchAutoComplete is told what to search. Which list is offered is said by {@link ItemConfigurationDropdown.load}, with the link a stock configuration names it by — see
 * `StockConfiguration.itemConfigurations` — and the dropdown offers nothing until it has been told one.
 *
 * Nothing is picked here: the engine picks none, and the configuration waits on the user's pick the way it waits on an answer — unless the item comes in only one, which the machine picks for the user when the position is made. Which one the configuration holds is said by {@link ItemConfigurationDropdown.select}.
 *
 * As wide as the value column of the answers list it stands above, since that is the column it sits in, and its list twice that, since a code and a name together do not fit in the column.
 */
export default class ItemConfigurationDropdown extends Dropdown {
	/** The items the configurations are offered as, kept so the one a configuration holds can be picked among them by identity. */
	#items: Array<DropdownItem>;
	#list: ListConfigurations | null;
	/** The fetch under way, or null while none is: answered, failed, or never made. */
	#loading: Promise<void> | null;
	/** The link the fetch under way is for, which is what tells its answer from one it was overtaken by. */
	#loadingSource: string | null;
	/** The link whose configurations are on offer, or null while none are. */
	#source: string | null;
	public constructor() {
		super();
		this.placeholder = 'Konfiguration';
		this.width = 208;
		this.minWidth = 208;
		// twice the column, so a configuration's code and name are read whole in the list: the
		// column only has room for the start of them
		this.popoverWidth = 2 * 208;
		this.#items = [];
		this.#list = null;
		this.#loading = null;
		this.#loadingSource = null;
		this.#source = null;
	}

	/**
	 * The link whose configurations are on offer, or null while none are.
	 *
	 * Set once they have arrived and not when they are asked for, so a caller can tell whether what is offered belongs to the configuration it is showing.
	 */
	public get source(): string | null {
		return this.#source;
	}

	/**
	 * What the dropdown asks for the configurations it offers, or null while nothing has been set.
	 *
	 * Null is a dropdown that has not been told where its configurations come from, and so offers none.
	 */
	public set list(value: ListConfigurations | null) {
		this.#list = value;
	}

	public get list(): ListConfigurations | null {
		return this.#list;
	}

	/** The configuration picked, or null while none is: before the configurations have arrived, before one has been picked, and for an item that comes in none. */
	public get selectedConfiguration(): SearchResult | null {
		const value = this.selectedItem?.value;
		return value instanceof SearchResult ? value : null;
	}

	/**
	 * Stops offering anything, and forgets any fetch still out.
	 *
	 * For a configuration that names no list — a position that is not a stock position, or an item that comes in no configurations — so that what the dropdown says belongs to nothing at all rather than to what was there. A fetch still out lands nowhere: it is no longer the one under way.
	 */
	public clear(): void {
		this.#items = [];
		this.#loading = null;
		this.#loadingSource = null;
		this.#source = null;
		this.data = null;
	}

	/**
	 * Fetches the configurations listed at `source`, and offers them with none picked.
	 *
	 * What another list said is taken off straight away, so nothing is offered for one item that belongs to another while the fetch is out. Asked for the list already on offer it does nothing, and asked again for the list being fetched it waits for that fetch rather than making a second. A fetch overtaken by one for
	 * another list lands nowhere, so the configurations on offer are always the last list's that was asked for.
	 *
	 * A fetch that fails is forgotten, and so is being asked with no list set, which starts none: the next time the dropdown is asked it tries again rather than offering nothing for good.
	 */
	public load(source: string): Promise<void> {
		if (source === this.#source) {
			return Promise.resolve();
		}
		if (this.#loading !== null && source === this.#loadingSource) {
			return this.#loading;
		}
		this.clear();
		if (this.#list === null) {
			return Promise.resolve();
		}
		this.#loadingSource = source;
		this.#loading = this.#fetch(this.#list, source);
		return this.#loading;
	}

	/**
	 * Picks the configuration whose code is `key` — `K001` — or none, for null or a code that is not on offer.
	 *
	 * Found among the items on offer by the code each was listed with, so it picks nothing before they have arrived: whoever calls it then calls it again once they have — see {@link ItemConfigurationDropdown.load}.
	 *
	 * None is picked by handing the list its items again rather than through `selectedItem`, for the reason `ModelTypeDropdown.reset` gives: the list drops its whole selection when its data is replaced, where the setter only reaches a pick it made itself, and a pick here may be the user's. Cleared to null first, since
	 * the list ignores data it is already holding — and only when something is picked, so a dropdown that picks none already says nothing.
	 */
	public select(key: string | null): void {
		const picked = this.#items.find((item) => (item.value as SearchResult).key === key);
		if (picked !== undefined) {
			this.selectedItem = picked;
			return;
		}
		if (this.selectedItem !== null) {
			this.data = null;
			this.data = this.#items;
		}
	}

	/** Fetches the configurations at `source` with `list` and offers them, unless the fetch has been overtaken or cleared by the time they arrive. */
	async #fetch(list: ListConfigurations, source: string): Promise<void> {
		const answer = await list(source);
		if (source !== this.#loadingSource) {
			return;
		}
		this.#loading = null;
		this.#loadingSource = null;
		if (!Array.isArray(answer)) {
			return;
		}
		this.#source = source;
		this.#items = answer.map(toItem);
		this.data = this.#items;
	}
}

/**
 * A configuration as the dropdown offers it: its code and its name.
 *
 * Both, because neither says it alone: the code is what the store knows it by, and two configurations can share a name — `K001` and `K100` are both a blind mounted on the frame.
 */
function toItem(configuration: SearchResult): DropdownItem {
	return new DropdownItem({ text: `${configuration.key} – ${configuration.name}`, value: configuration });
}
customElements.define('cf-item-configuration-dropdown', ItemConfigurationDropdown);
